import { StateCreator } from 'zustand'
import { invoiceApi } from '../../lib/api'
import { useAuthStore } from '../authStore'
import { mergeInvoices } from '../../utils/invoiceMerge'
import { ensureInvoiceId } from '../../utils/invoiceCalculations'
import type { InvoiceStore } from '../invoiceStore'
import type { Invoice } from '@dutchinvoice/types'
import type { LoadingState, StoreError, SyncedInvoice } from '../../types/invoice'

// Helper functions for local storage
const getLocalInvoices = (): SyncedInvoice[] => {
    const stored = localStorage.getItem('dutchinvoice_local_invoices')
    return stored ? JSON.parse(stored) : []
}

export interface InvoiceSyncSlice {
    invoices: SyncedInvoice[]
    loadingState: LoadingState
    error: StoreError | null
    lastSyncTime: number | null

    loadInvoices: () => Promise<void>
    syncWithCloud: () => Promise<void>

    // Local operations
    addLocalInvoice: (invoice: Invoice) => void
    updateLocalInvoice: (id: string, updates: Partial<Invoice>) => void
    removeLocalInvoice: (id: string) => void
    markAsSynced: (id: string) => void
    clearError: () => void
}

export const createInvoiceSyncSlice: StateCreator<
    InvoiceStore,
    [],
    [],
    InvoiceSyncSlice
> = (set, get) => ({
    invoices: [],
    loadingState: 'idle' as LoadingState,
    error: null,
    lastSyncTime: null,

    loadInvoices: async () => {
        const { loadingState } = get()

        // Prevent duplicate loads
        if (loadingState === 'loading_local' ||
            loadingState === 'loading_cloud') {
            return
        }

        try {
            // Start with local loading
            set({ loadingState: 'loading_local' as LoadingState })

            // Load from localStorage
            const stored = localStorage.getItem('dutchinvoice_local_invoices')
            const localInvoices = stored ? JSON.parse(stored) : []

            // No longer converting dates to localized format here
            // This ensures dates remain in universal format in the state

            // Update state with local data
            set({
                savedInvoices: localInvoices,
                invoices: localInvoices,
                loadingState: 'idle' as LoadingState
            })

            // Only sync with cloud if user is authenticated
            if (useAuthStore.getState().isAuthenticated) {
                await get().syncWithCloud()
            }

        } catch (error) {
            set({
                loadingState: 'error' as LoadingState,
                error: {
                    code: 'LOCAL_LOAD_ERROR',
                    message: 'Failed to load local invoices',
                    details: error
                }
            })
        }
    },

    syncWithCloud: async () => {
        const { loadingState, currentPeriod } = get()

        // Prevent duplicate syncs and only sync if authenticated
        if (loadingState === 'syncing' || !useAuthStore.getState().isAuthenticated) {
            return
        }

        try {
            set({ loadingState: 'syncing' as LoadingState })

            // Get cloud invoices with period filtering
            const cloudInvoices = await invoiceApi.sync({ period: currentPeriod });

            // Merge with local invoices using the imported merge function
            const localInvoices = getLocalInvoices();
            const mergedInvoices = mergeInvoices(cloudInvoices, localInvoices, { debug: true });

            // Update state - keep all invoices in savedInvoices
            set({
                invoices: mergedInvoices,
                savedInvoices: mergedInvoices,
                loadingState: 'idle' as LoadingState,
                lastSyncTime: Date.now(),
                error: null
            })

            // Update localStorage
            localStorage.setItem('dutchinvoice_local_invoices', JSON.stringify(mergedInvoices))

        } catch (error) {
            set({
                loadingState: 'error' as LoadingState,
                error: {
                    code: 'SYNC_ERROR',
                    message: 'Failed to sync with cloud',
                    details: error
                }
            })
        }
    },

    addLocalInvoice: (invoice) => {
        set(state => {
            const syncedInvoice = ensureInvoiceId(invoice) as SyncedInvoice;
            const newInvoices = [...state.invoices, syncedInvoice];
            const newSavedInvoices = [...state.savedInvoices, syncedInvoice];
            return { invoices: newInvoices, savedInvoices: newSavedInvoices };
        });
    },

    updateLocalInvoice: (id, updates) => {
        set(state => {
            const newInvoices = state.invoices.map(inv =>
                inv.id === id ? { ...inv, ...updates, synced: false } : inv
            )

            const newSavedInvoices = state.savedInvoices.map(inv =>
                inv.id === id ? { ...inv, ...updates, synced: false } : inv
            )

            // Update localStorage
            localStorage.setItem('dutchinvoice_local_invoices', JSON.stringify(newInvoices))

            return {
                invoices: newInvoices,
                savedInvoices: newSavedInvoices
            }
        })
    },

    removeLocalInvoice: (id) => {
        set(state => {
            const newInvoices = state.invoices.filter(inv => inv.id !== id)
            const newSavedInvoices = state.savedInvoices.filter(inv => inv.id !== id)

            // Update localStorage
            localStorage.setItem('dutchinvoice_local_invoices', JSON.stringify(newInvoices))

            return {
                invoices: newInvoices,
                savedInvoices: newSavedInvoices
            }
        })
    },

    markAsSynced: (id) => {
        set(state => {
            const newInvoices = state.invoices.map(inv =>
                inv.id === id ? { ...inv, synced: true } : inv
            )

            const newSavedInvoices = state.savedInvoices.filter(inv => inv.id !== id)

            // Update localStorage
            localStorage.setItem('dutchinvoice_local_invoices', JSON.stringify(newInvoices))

            return {
                invoices: newInvoices,
                savedInvoices: newSavedInvoices
            }
        })
    },

    clearError: () => set({ error: null }),
}) 